/* /review/:docId — the review workspace.
 *
 * Three panes: the findings, the selected finding, and the document. Around
 * them, only what a working session needs — the document's name, how far
 * through it the reviewer is, and the way to the comment sheet. IA.md is
 * explicit that marketing navigation does not appear inside a working session,
 * so AppShell renders bare here and this header is the only one.
 *
 * A check that cannot run until the reviewer confirms something is a row in the
 * same list, in the plan area it belongs to, with the confirmation inside the
 * card that opens when it is selected. Nothing is asked for up front: the
 * reviewer meets the ask having already read what the other checks found.
 *
 * The rules this page runs on — grouping, ordering, the two filter rows, the
 * keyboard order, what it opens on — are in ./workspaceModel.ts, and the ones
 * about the inline confirmation are in ../components/workspace/reconcileModel.ts,
 * so they can be asserted without a browser. What is left here is loading,
 * persistence and layout. */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button, Disclosure, Label, Spinner } from '../components/primitives';
import { FindingCard } from '../components/finding';
import { GlossaryProvider } from '../components/Glossary';
import {
  AnswerSetAside,
  CheckNotRun,
  CheckTuning,
  DeleteReview,
  DocumentPane,
  FindingsRail,
  InlineRateAnswer,
  InlineReconcile,
  PendingCheckCard,
  ParseDetail,
  PrintReview,
  RATE_SKIP_ID,
  RECONCILE_CHECK_ID,
  RECONCILE_ROW_ID,
  ReviewDetails,
  isPendingRowId,
  picksFrom,
  rateAnswerable,
  reconcileRow,
  selectionFrom,
  skippedCheckRows,
  type ReconcilePicks,
} from '../components/workspace';
import {
  PATTERNS_CHECK_ID,
  PATTERNS_SKIP,
  clearRun,
  runFor,
  stillCheckingLine,
} from '../data/analysisRun';
import type { AnalysisRunState } from '../data/analysisRun';
import { getSeededReport } from '../data/report';
import { getSeededPages, hasSeededPages } from '../data/seed/pages';
import {
  addDismissals,
  commentFor,
  muteCandidate,
  mutePromptKey,
  mutedCheck,
  newReviewRecord,
} from '../data/review';
import type { DismissalTally, MuteCandidate } from '../data/review';
import {
  declineMutePrompt,
  deleteReview,
  getDismissalTally,
  getDocument,
  getFindings,
  getMutePrompts,
  getMutedChecks,
  getPages,
  getReview,
  getUiPrefs,
  putFindings,
  setMutedCheck,
  setUiPrefs,
  storageAvailable,
  updateReview,
} from '../data/store';
import { runDeterministic } from '../engine/deterministic';
import type { ReconcileSelection } from '../engine/deterministic';
import {
  filterFindings,
  groupByPlanArea,
  initialSelection,
  nothingFoundLine,
  partitionMuted,
  progressOf,
  type DecisionFilter,
} from './workspaceModel';
import type {
  CheckFailure,
  CheckSkip,
  Disposition,
  EvidenceFilter,
  ExtractedPages,
  FailureModeId,
  Flag,
  HeaderFields,
  MutePrompts,
  MutedChecks,
  ReviewRecord,
  SourceDocument,
} from '../types';
import './Workspace.css';

/** Everything the workspace needs about the document under review. */
interface WorkspaceDoc {
  id: string;
  /** What the header calls it. */
  name: string;
  findings: Flag[];
  /** The parse, when this review was made from a file the reviewer opened. */
  source: SourceDocument | null;
  /** Which checks ran to completion, so a silent check can be told apart. */
  ranChecks: string[];
  skipped: CheckSkip[];
  /** The tables the reviewer has confirmed, where they have confirmed any. */
  reconcileSelection?: ReconcileSelection;
  /** Checks that stopped part way. Reported, never swallowed. */
  failures: CheckFailure[];
  /** True while checks are still arriving from a run started on /new. */
  streaming: boolean;
  /**
   * What stopped, when a run did not finish. Whatever it found before that is
   * kept: a reviewer shown four findings must not lose them to the fifth check.
   */
  runError: string | null;
  /**
   * COPY.md's one line about how the worked example was produced. Shown under
   * the findings header on the example and nowhere else. Not hidden, not
   * expandable.
   */
  provenance: string | null;
}

type Load =
  | { state: 'loading'; step: string }
  | { state: 'unknown' }
  | { state: 'ready'; doc: WorkspaceDoc };

/** The example is bundled with the app, so it is ready on the first render. */
function initialLoad(docId: string | undefined): Load {
  const seeded = seededDoc(docId);
  if (seeded) return { state: 'ready', doc: seeded };
  // Nowhere to look. A browser with storage blocked should be told that
  // straight away rather than watching a spinner that cannot resolve.
  if (!storageAvailable()) return { state: 'unknown' };
  return { state: 'loading', step: 'Looking for this review in your browser' };
}

function seededDoc(docId: string | undefined): WorkspaceDoc | null {
  const report = getSeededReport(docId);
  if (!report) return null;
  return {
    id: report.id,
    name: report.document.facility ?? report.document.name,
    findings: report.flags,
    source: null,
    ranChecks: [],
    skipped: [],
    failures: [],
    streaming: false,
    runError: null,
    provenance: report.provenance,
  };
}

/**
 * The skips a re-run does not re-report: the clause-pattern stage, which is
 * never part of an arithmetic run and so would vanish from the list the first
 * time a reviewer confirmed a table.
 */
function patternSkips(skipped: CheckSkip[]): CheckSkip[] {
  const found = skipped.filter((s) => s.checkId === PATTERNS_CHECK_ID);
  return found.length > 0 ? found : [PATTERNS_SKIP];
}

/** What the workspace shows while a run started on /new is still filling it. */
function docFromRun(state: AnalysisRunState): WorkspaceDoc | null {
  if (!state.document) return null;
  return {
    id: state.document.id,
    name: state.document.fileName,
    findings: state.flags,
    source: state.document,
    ranChecks: state.ranChecks,
    skipped: state.skipped,
    failures: state.failures,
    streaming: state.status === 'running',
    runError: state.error ?? null,
    provenance: null,
  };
}

export default function Workspace() {
  const { docId } = useParams();
  // The comment sheet's "Show the finding" links here with the finding in the
  // fragment. It is the address of a finding, so it wins over both the stored
  // reading position and the derived selection — the reviewer asked for this
  // one. A fragment naming a finding that is not here falls through to the
  // ordinary selection rather than emptying the pane; see `chosenExists`.
  const requestedId = decodeURIComponent(useLocation().hash.replace(/^#/, ''));
  const [load, setLoad] = useState<Load>(() => initialLoad(docId));
  const [review, setReview] = useState<ReviewRecord | null>(null);
  // Null until the reviewer picks one. What is on screen before that is
  // derived rather than stored, so the first finding is selected in the first
  // render rather than after an effect — see `selectedId` below.
  const [chosenId, setChosenId] = useState<string | null>(requestedId || null);
  const [documentPaneOpen, setDocumentPaneOpen] = useState(true);
  /* Below 1024px the three panes become one column and two of them become
     sheets: the list opens from a button on the narrow bar, the document opens
     from a finding's citation. Both are ephemeral view state — nothing about
     the review — so neither is stored, and above 1024px the classes they set
     mean nothing because the media query does not read them.

     The sheets are deliberately not modal. BRIEF.md forbids a focus trap, so
     what they do instead is move focus in on open, hand it back on close, and
     answer Escape; nothing behind them is hidden from the keyboard, because a
     closed sheet is `display: none` and has nothing focusable in it. */
  const [findingsSheetOpen, setFindingsSheetOpen] = useState(false);
  const [documentSheetOpen, setDocumentSheetOpen] = useState(false);
  const findingsTrigger = useRef<HTMLButtonElement>(null);
  const railElement = useRef<HTMLDivElement>(null);
  const centreElement = useRef<HTMLElement>(null);
  const sheetIds = useId();
  const railId = `${sheetIds}-findings`;
  const documentSheetId = `${sheetIds}-document`;
  // The page text the document pane reads. Loaded once per document, apart
  // from the parse: the pane opens on one page and should not have to pull a
  // whole SourceDocument — rawText, sections, tables — out of storage to do it.
  const [pages, setPages] = useState<ExtractedPages | null>(null);
  const [pagesLoading, setPagesLoading] = useState(true);
  // Non-zero while a "take me to the span" request from the reviewer is
  // outstanding. Following a citation is the one case where the pane takes
  // focus; selecting a finding moves the pane and announces it but leaves focus
  // in the list. The pane clears the request once it has answered it, so
  // reopening a collapsed pane cannot replay it. See docs/redesign/REPORT.md.
  const [focusRequest, setFocusRequest] = useState(0);
  // Muting, and the offers the reviewer has already answered. Both are per
  // browser rather than per review — they are facts about how this reviewer
  // works — so they are loaded once and not with the document.
  const [muted, setMuted] = useState<MutedChecks>({});
  const [mutePrompts, setMutePrompts] = useState<MutePrompts>({});
  // Dismissals made in every *other* review in this browser. The one on screen
  // is added live below, so a third dismissal made in this session raises the
  // offer without waiting for a reload.
  const [otherDismissals, setOtherDismissals] = useState<DismissalTally>({});
  const [mutedOpen, setMutedOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  /* --- loading ---------------------------------------------------------- */

  useEffect(() => {
    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    (async () => {
      if (!docId) {
        setLoad({ state: 'unknown' });
        return;
      }

      const seeded = seededDoc(docId);
      if (seeded) setLoad({ state: 'ready', doc: seeded });
      else if (!storageAvailable()) {
        // Nowhere to look, and `initialLoad` has already said so. Flipping to
        // a spinner here would show a browser with storage blocked a loading
        // state that cannot resolve into anything but the answer it already
        // has.
        setLoad({ state: 'unknown' });
        return;
      } else {
        setLoad({ state: 'loading', step: 'Looking for this review in your browser' });
      }

      // The reviewer's decisions come first: they are the product's output, and
      // they exist for the example as well as for an ingested document.
      try {
        const stored = await getReview(docId);
        if (!cancelled && stored) setReview(stored);
      } catch {
        // Storage unavailable. Decisions will hold for the session only.
      }

      if (seeded || cancelled) return;

      // A run started on /new that is still filling. Its findings are already
      // guarded and already in order, and its document is in memory, so the
      // workspace reads them from it rather than racing storage for a copy.
      const live = runFor(docId);
      if (live) {
        const apply = (state: AnalysisRunState) => {
          const next = docFromRun(state);
          if (next) setLoad({ state: 'ready', doc: next });
          if (state.status !== 'running') clearRun(docId);
        };
        apply(live.state);
        const unsubscribe = live.subscribe((state) => {
          if (!cancelled) apply(state);
        });
        unsubscribers.push(unsubscribe);
        return;
      }

      try {
        const doc = await getDocument(docId);
        if (cancelled) return;
        if (!doc) {
          setLoad({ state: 'unknown' });
          return;
        }

        let findings = await getFindings(docId);
        if (cancelled) return;

        // A document that has been read but never checked. Nothing is gained by
        // making the reviewer ask for the checks, so they run on arrival.
        if (!findings) {
          setLoad({
            state: 'loading',
            step: `Checking ${doc.fileName} against the checks that need no confirmation`,
          });
          const run = await runDeterministic(doc, {
            awardDate: doc.metadata.awardDate,
          });
          if (cancelled) return;
          findings = {
            docId,
            createdAt: new Date().toISOString(),
            flags: run.flags,
            ranChecks: run.ranChecks,
            // The clause-pattern stage is not one of the arithmetic checks, so
            // the run does not report it. It still did not happen, and a stage
            // that did not happen is named here as everywhere else.
            skipped: [...run.skipped, PATTERNS_SKIP],
          };
          try {
            await putFindings(findings);
          } catch {
            // Persisting is a convenience; the findings are on screen either way.
          }
        }

        if (cancelled) return;
        setLoad({
          state: 'ready',
          doc: {
            id: doc.id,
            name: doc.fileName,
            findings: findings.flags,
            source: doc,
            ranChecks: findings.ranChecks ?? [],
            skipped: findings.skipped ?? [],
            reconcileSelection: findings.reconcileSelection,
            failures: [],
            streaming: false,
            runError: null,
            provenance: null,
          },
        });
      } catch {
        if (!cancelled) setLoad({ state: 'unknown' });
      }
    })();

    return () => {
      cancelled = true;
      for (const unsubscribe of unsubscribers) unsubscribe();
    };
  }, [docId]);

  /* --- the page text the document pane shows -----------------------------
     The worked example ships its pages as a lazily loaded chunk, because it
     never goes through ingestion and so has nothing in this browser to read.
     Every other review reads `pages:<docId>`, written when the file was
     ingested. Either way nothing is re-parsed and no PDF is opened here. */

  useEffect(() => {
    let cancelled = false;
    setPages(null);
    setPagesLoading(true);

    (async () => {
      if (!docId) {
        if (!cancelled) setPagesLoading(false);
        return;
      }
      try {
        const found = hasSeededPages(docId)
          ? await getSeededPages(docId)
          : await getPages(docId);
        if (!cancelled) setPages(found ?? null);
      } catch {
        // The pane says the page text is not in this browser, which is true.
        if (!cancelled) setPages(null);
      } finally {
        if (!cancelled) setPagesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [docId]);

  /* --- the document pane's collapsed state, per browser ------------------ */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const prefs = await getUiPrefs();
        if (!cancelled) setDocumentPaneOpen(prefs.documentPaneOpen);
      } catch {
        // The default is open.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* --- the checks this reviewer has quieted, per browser ----------------- */

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [checks, prompts, tally] = await Promise.all([
          getMutedChecks(),
          getMutePrompts(),
          getDismissalTally(docId),
        ]);
        if (cancelled) return;
        setMuted(checks);
        setMutePrompts(prompts);
        setOtherDismissals(tally);
      } catch {
        // Nothing is muted, which is the honest default.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  const toggleDocumentPane = useCallback((open: boolean) => {
    setDocumentPaneOpen(open);
    void setUiPrefs({ documentPaneOpen: open }).catch(() => undefined);
  }, []);

  /* --- writing the review ----------------------------------------------- */

  const doc = load.state === 'ready' ? load.doc : null;
  const findings = doc?.findings ?? [];
  const dispositions = useMemo(() => review?.dispositions ?? {}, [review]);

  const write = useCallback(
    (change: (record: ReviewRecord) => ReviewRecord) => {
      if (!docId) return;
      // Optimistic, so a decision never waits on the database.
      setReview((current) => change(current ?? newReviewRecord(docId, doc?.name ?? '')));
      void updateReview(docId, doc?.name ?? '', change)
        .then((next) => setReview(next))
        .catch(() => undefined);
    },
    [docId, doc?.name],
  );

  const setDisposition = useCallback(
    (findingId: string, next: Disposition | null) => {
      // Deciding pins the selection. Without this the derived selection would
      // move to the next undecided finding the moment a decision was made, and
      // the card would change identity under the reviewer who just decided it.
      setChosenId(findingId);
      write((record) => {
        const dispositionsNext = { ...record.dispositions };
        if (next) dispositionsNext[findingId] = next;
        else delete dispositionsNext[findingId];
        return { ...record, dispositions: dispositionsNext };
      });
    },
    [write],
  );

  const setComment = useCallback(
    (findingId: string, next: string | null) =>
      write((record) => {
        const comments = { ...record.editedComments };
        // null is "back to the generated draft" — the absence of an edit, not
        // an edit that happens to match it.
        if (next === null) delete comments[findingId];
        else comments[findingId] = next;
        return { ...record, editedComments: comments };
      }),
    [write],
  );

  const setHeaderFields = useCallback(
    (patch: Partial<HeaderFields>) =>
      write((record) => ({
        ...record,
        headerFields: { ...record.headerFields, ...patch },
      })),
    [write],
  );

  const setPane = useCallback(
    (patch: Partial<ReviewRecord['paneState']>) =>
      write((record) => ({ ...record, paneState: { ...record.paneState, ...patch } })),
    [write],
  );

  /** Pressing, or un-pressing, "Skip for now" on a check that needs an answer. */
  const setCheckSkipped = useCallback(
    (checkId: string, skipped: boolean) =>
      write((record) => {
        const set = new Set(record.skippedChecks ?? []);
        if (skipped) set.add(checkId);
        else set.delete(checkId);
        return { ...record, skippedChecks: [...set] };
      }),
    [write],
  );

  const select = useCallback(
    (id: string) => {
      setChosenId(id);
      // The pane follows the finding: a page the reviewer had navigated to is
      // about the finding they were on, not about this one.
      //
      // A check waiting on an answer is a row in the same list but is not a
      // finding, so it is never written into the resume position — a stored
      // position has to name something that will still be there next time.
      setPane({
        selectedFindingId: isPendingRowId(id) ? null : id,
        documentPage: null,
      });
    },
    [setPane],
  );

  /** The reviewer moved the pane itself, which pins it until they select again. */
  const showPage = useCallback(
    (next: number) => setPane({ documentPage: next }),
    [setPane],
  );

  /**
   * Following a finding's citation. The pane opens on the cited page and takes
   * focus at the span, which is the one case where focus leaves the list: the
   * reviewer asked to be taken there, and "Back to the finding" brings them
   * back.
   */
  const showInDocument = useCallback(
    (finding: Flag) => {
      if (!documentPaneOpen) toggleDocumentPane(true);
      // On one column the pane is a sheet from the bottom of the window, and
      // the citation is what opens it. Harmless at three panes: the class it
      // sets is only read below 1024px.
      setDocumentSheetOpen(true);
      setPane({ documentPage: finding.page });
      setFocusRequest((request) => request + 1);
    },
    [documentPaneOpen, setPane, toggleDocumentPane],
  );

  const clearFocusRequest = useCallback(() => setFocusRequest(0), []);

  /**
   * Selecting from the list. On one column that also closes the sheet the list
   * was in and puts focus on the finding chosen — the reviewer asked for it, so
   * sending them back to the button they opened the list with would be wrong.
   */
  const selectFromList = useCallback(
    (id: string) => {
      select(id);
      if (!findingsSheetOpen) return;
      setFindingsSheetOpen(false);
      requestAnimationFrame(() => {
        const title = document.getElementById(`${id}-title`);
        (title ?? centreElement.current)?.focus();
      });
    },
    [select, findingsSheetOpen],
  );

  /* --- the two sheets, below 1024px --------------------------------------
     Each of them moves focus in when it opens and hands it back when it
     closes, so a reviewer working from the keyboard is never left standing on
     a control that is no longer on screen. */

  const openFindingsSheet = useCallback(() => setFindingsSheetOpen(true), []);

  /** Closed without choosing anything: focus goes back to the button. */
  const dismissFindingsSheet = useCallback(() => {
    setFindingsSheetOpen(false);
    findingsTrigger.current?.focus();
  }, []);

  /** Closed by the document's own control, or by Escape over it. */
  const dismissDocumentSheet = useCallback(() => {
    setDocumentSheetOpen(false);
    // Back where the citation was followed from, which is the open finding.
    // Read out of the centre pane rather than from the selection, so this does
    // not have to be recreated every time the reviewer moves down the list.
    const centre = centreElement.current;
    const title = centre?.querySelector<HTMLElement>('.fc__claim-title');
    (title ?? centre)?.focus();
  }, []);

  /* The list has focus when it opens, on the row the reviewer is on, so j and
     k work without a Tab first. If there is no row to land on — every finding
     filtered away — the sheet's own control takes it instead. A frame is
     waited for so the rows exist to be found. */
  useEffect(() => {
    if (!findingsSheetOpen) return;
    const frame = requestAnimationFrame(() => {
      const rail = railElement.current;
      if (!rail) return;
      const row =
        rail.querySelector<HTMLElement>('.fr__row[aria-current="true"]') ??
        rail.querySelector<HTMLElement>('.ws__panel-close');
      row?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [findingsSheetOpen]);

  /**
   * Escape closes whichever sheet is on top. Handled on the workspace rather
   * than on each sheet so it works from anywhere inside one, including from
   * the document pane's own page controls.
   */
  const onWorkspaceKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Escape') return;
      if (documentSheetOpen) {
        event.preventDefault();
        dismissDocumentSheet();
        return;
      }
      if (findingsSheetOpen) {
        event.preventDefault();
        dismissFindingsSheet();
      }
    },
    [documentSheetOpen, findingsSheetOpen, dismissDocumentSheet, dismissFindingsSheet],
  );

  // Arriving from the comment sheet, or following the same link a second time
  // after selecting something else in between.
  useEffect(() => {
    if (requestedId) setChosenId(requestedId);
  }, [requestedId]);

  /** The way back out of the pane, for a reviewer working without a mouse. */
  const backToFinding = useCallback((findingId: string) => {
    document.getElementById(`${findingId}-title`)?.focus();
  }, []);

  /* --- the tables the reviewer confirms ---------------------------------
     The engine proposes and the reviewer confirms; nothing is computed from a
     proposal. `picks` holds the answers before they are confirmed, seeded from
     whatever was confirmed last time so that answering one further question
     does not ask for the first two again. */

  const tables = doc?.source?.tables ?? null;
  const [picks, setPicks] = useState<ReconcilePicks | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!tables) {
      setPicks(null);
      return;
    }
    setPicks(picksFrom(doc?.reconcileSelection, tables));
    // Re-seeded when the document changes or when a confirmed selection lands,
    // and not on every render of the same one.
  }, [tables, doc?.reconcileSelection]);

  /**
   * Runs every arithmetic check again, this time with the tables the reviewer
   * has confirmed. Confirming is the last thing the reconciliation check was
   * waiting for, so this is also where the findings record gains the selection
   * it was produced from — answering one further question later re-runs from
   * here and must not lose the answers already given.
   */
  const runWithSelection = useCallback(
    async (selection: ReconcileSelection, land: 'first-finding' | 'stay') => {
      const source = doc?.source;
      if (!source || checking) return;
      setChecking(true);
      try {
        const run = await runDeterministic(source, {
          reconcileSelection: selection,
          awardDate: source.metadata.awardDate,
        });
        const record = {
          docId: source.id,
          createdAt: new Date().toISOString(),
          flags: run.flags,
          ranChecks: run.ranChecks,
          // A re-run reports the arithmetic checks again. The stage that never
          // runs is not one of them, so it is carried across rather than
          // silently dropped by the re-run that did not consider it.
          skipped: [...run.skipped, ...patternSkips(doc?.skipped ?? [])],
          reconcileSelection: selection,
        };
        try {
          await putFindings(record);
        } catch {
          // Persisting is a convenience; the findings are on screen either way.
        }
        setLoad((current) =>
          current.state === 'ready'
            ? {
                state: 'ready',
                doc: {
                  ...current.doc,
                  findings: run.flags,
                  ranChecks: run.ranChecks,
                  skipped: [...run.skipped, ...patternSkips(current.doc.skipped)],
                  failures: run.failures,
                  reconcileSelection: selection,
                },
              }
            : current,
        );
        // Answering is the opposite of skipping.
        setCheckSkipped(RECONCILE_CHECK_ID, false);
        if (land === 'first-finding') {
          const first = run.flags.find((f) => f.checkId === RECONCILE_CHECK_ID);
          // No disagreement is a result, not an absence of one, so the row
          // saying so is what the reviewer lands on.
          setChosenId(first ? first.id : RECONCILE_ROW_ID);
        }
      } finally {
        setChecking(false);
      }
    },
    [doc?.source, checking, setCheckSkipped],
  );

  const setAside = useMemo(() => review?.skippedChecks ?? [], [review]);

  const pendingRow = useMemo(
    () =>
      reconcileRow({
        tables,
        ranChecks: doc?.ranChecks ?? [],
        skipped: doc?.skipped ?? [],
        findings,
        setAside,
      }),
    [tables, doc?.ranChecks, doc?.skipped, findings, setAside],
  );

  /**
   * The rows beneath the plan areas: the check waiting on two answers, then
   * every check that did not run at all. A check that could not run must never
   * look like a check that ran and found nothing, so each one is named, in its
   * own group, with the reason and what it went looking for.
   */
  const pending = useMemo(
    () => [
      ...(pendingRow ? [pendingRow] : []),
      ...skippedCheckRows(doc?.skipped ?? [], doc?.failures ?? []),
    ],
    [pendingRow, doc?.skipped, doc?.failures],
  );

  /* --- where the reviewer is --------------------------------------------
     Until they pick one, the selection is derived: the finding they were last
     on if it still exists, otherwise the first they have not decided. Derived
     rather than set by an effect so the workspace opens on a finding in its
     first render, and so a stored position arriving late still lands. */

  const decisionFilter: DecisionFilter = review?.paneState.decisionFilter ?? 'all';
  const evidenceFilter: EvidenceFilter = review?.paneState.evidenceFilter ?? 'all';

  // Muted checks come out of the plan areas and into their own collapsed group
  // at the bottom. They are not removed: the counter below still counts them,
  // because a progress line that reached "10 of 10" while two findings sat
  // undecided out of sight would be hiding work from a federal reviewer.
  const split = useMemo(() => partitionMuted(findings, muted), [findings, muted]);
  const visible = useMemo(
    () => filterFindings(split.active, dispositions, decisionFilter, evidenceFilter),
    [split.active, dispositions, decisionFilter, evidenceFilter],
  );
  const groups = useMemo(
    () => groupByPlanArea(split.active, visible),
    [split.active, visible],
  );
  const progress = useMemo(
    () => progressOf(findings, dispositions),
    [findings, dispositions],
  );

  // A chosen row that no longer exists — the check the reviewer was looking at
  // has just produced findings of its own — falls back rather than leaving the
  // pane empty.
  const chosenExists =
    chosenId !== null &&
    (findings.some((f) => f.id === chosenId) ||
      pending.some((row) => row.id === chosenId));
  const selectedId = chosenExists
    ? chosenId
    : (initialSelection(
        split.active,
        dispositions,
        review?.paneState.selectedFindingId ?? null,
      ) ??
      // Nothing found yet, but a check still waiting on an answer is work. It
      // is what the reviewer lands on rather than an empty pane.
      pending[0]?.id ??
      null);
  const selected = findings.find((f) => f.id === selectedId) ?? null;
  const selectedPending = pending.find((row) => row.id === selectedId) ?? null;

  /** The finding on screen is waiting on one further answer, and it can be given. */
  const rateAsk =
    selected?.pendingAnswer?.question === 'rate-table' &&
    tables !== null &&
    rateAnswerable(tables)
      ? selected.pendingAnswer
      : null;
  const includedCount = Object.values(dispositions).filter(
    (d) => d.decision === 'include',
  ).length;

  /* --- the third loop: the same check, set aside for the same reason -------
     Three times is enough to be a pattern and few enough that a reviewer meets
     it inside one plan. What comes of it is an offer, never an action; the
     rule itself is muteCandidate() in src/data/review.ts. */

  const tuning: MuteCandidate | null = useMemo(() => {
    const modes = new Map(findings.map((f) => [f.id, f.failureModeId]));
    const tally = addDismissals(otherDismissals, dispositions, (id) =>
      modes.get(id),
    );
    return muteCandidate(tally, muted, mutePrompts);
  }, [otherDismissals, dispositions, findings, muted, mutePrompts]);

  const muteCheck = useCallback((candidate: MuteCandidate) => {
    const entry = mutedCheck(
      candidate.failureModeId as FailureModeId,
      candidate.reason,
      candidate.count,
    );
    setMuted((current) => ({ ...current, [entry.failureModeId]: entry }));
    // Opened, not left collapsed: the findings that just moved must be visibly
    // somewhere rather than appearing to have been deleted.
    setMutedOpen(true);
    void setMutedCheck(entry)
      .then(setMuted)
      .catch(() => undefined);
  }, []);

  const keepShowing = useCallback((candidate: MuteCandidate) => {
    const key = mutePromptKey(candidate.failureModeId, candidate.reason);
    setMutePrompts((current) => ({ ...current, [key]: candidate.count }));
    void declineMutePrompt(key, candidate.count)
      .then(setMutePrompts)
      .catch(() => undefined);
  }, []);

  /* --- the second loop's other half: leaving nothing behind --------------- */

  const removeReview = useCallback(async () => {
    if (!docId || deleting) return;
    setDeleting(true);
    try {
      await deleteReview(docId);
    } catch {
      // Nothing stored, or nowhere to delete it from. Either way there is
      // nothing left to take away.
    }
    navigate('/new', { replace: true });
  }, [docId, deleting, navigate]);

  /* --- states before the workspace --------------------------------------- */

  if (load.state === 'loading') {
    return (
      <>
        <Wordmark />
        <div className="ws__loading">
          <Spinner label="Working" />
          <p className="ws__loading-step">{load.step}</p>
        </div>
      </>
    );
  }

  if (load.state === 'unknown' || !doc) return <UnknownReview />;

  /* --- the workspace ------------------------------------------------------ */

  return (
    <GlossaryProvider>
      <div
        className={[
          'ws',
          documentPaneOpen ? null : 'ws--doc-closed',
          findingsSheetOpen ? 'ws--findings-sheet' : null,
          documentSheetOpen ? 'ws--document-sheet' : null,
        ]
          .filter(Boolean)
          .join(' ')}
        data-doc={doc.id}
        onKeyDown={onWorkspaceKeyDown}
      >
        <header className="ws__head">
          <Link to="/" className="ws__lockup">
            Ceiling
          </Link>
          {/* The page's one h1. It is what the page is about, and it is
              already the right size and weight — see Workspace.css, where the
              rule is written against the class rather than the tag. */}
          <h1 className="ws__doc-name">{doc.name}</h1>
          <span className="ws__decided">{progress.label}</span>
          <Link className="ws__sheet" to={`/review/${doc.id}/comments`}>
            Comment sheet ({includedCount})
          </Link>
        </header>

        {doc.runError ? (
          <div className="ws__run-error" role="alert">
            <Label tone="amber">The checks did not all finish</Label>
            <p className="ws__run-error-body">{doc.runError}</p>
          </div>
        ) : null}

        <ReviewDetails
          fields={review?.headerFields ?? newReviewRecord(doc.id, doc.name).headerFields}
          onChange={setHeaderFields}
        >
          {/* The way out. Inside the strip that already holds everything else
              about this review, so it is one click from the top of the
              workspace and never one click from a finding. */}
          <DeleteReview
            stored={doc.source !== null}
            busy={deleting}
            onDelete={() => void removeReview()}
          />
        </ReviewDetails>

        {/* The offer to quiet a check, when the same reason has come up three
            times. Between the review's own strip and the panes, because it is
            about the review rather than about the finding on screen. */}
        {tuning ? (
          <CheckTuning
            candidate={tuning}
            onMute={() => muteCheck(tuning)}
            onKeep={() => keepShowing(tuning)}
          />
        ) : null}

        {/* Below 1024px the panes become one column and the list moves into a
            sheet. This bar is the way into it, and it carries nothing else —
            the same count and the same progress line the list itself opens
            with, so nothing is learned by opening it that was not already on
            screen. Absent above 1024px, where the list is simply there. */}
        <div className="ws__narrow-bar">
          <button
            type="button"
            className="ws__panel-open"
            ref={findingsTrigger}
            aria-expanded={findingsSheetOpen}
            aria-controls={railId}
            onClick={findingsSheetOpen ? dismissFindingsSheet : openFindingsSheet}
          >
            Findings ({findings.length})
          </button>
          <span className="ws__narrow-progress">{progress.label}</span>
        </div>

        <div className="ws__panes">
          <div
            className="ws__rail"
            id={railId}
            ref={railElement}
            data-scroll-pane
          >
            {/* The sheet's own title and dismiss. Absent above 1024px, where
                the rail is a column and has neither. */}
            <div className="ws__panel-bar">
              <span className="ws__panel-title">Findings</span>
              <button
                type="button"
                className="ws__panel-close"
                onClick={dismissFindingsSheet}
              >
                Close
              </button>
            </div>
            <FindingsRail
              findings={findings}
              groups={groups}
              dispositions={dispositions}
              progress={progress}
              selectedId={selectedId}
              onSelect={selectFromList}
              decisionFilter={decisionFilter}
              onDecisionFilter={(next) => setPane({ decisionFilter: next })}
              evidenceFilter={evidenceFilter}
              onEvidenceFilter={(next) => setPane({ evidenceFilter: next })}
              nothingFound={nothingFoundLine(findings)}
              pending={pending}
              provenance={doc.provenance}
              streaming={
                doc.streaming ? stillCheckingLine(findings.length) : null
              }
              muted={split.muted}
              mutedOpen={mutedOpen}
              onMutedOpenChange={setMutedOpen}
            />
          </div>

          <main
            className="ws__centre"
            ref={centreElement}
            tabIndex={-1}
            data-scroll-pane
          >
            {selected ? (
              // Keyed on the finding, so selecting one cross-fades the pane
              // rather than swapping its contents in place.
              <div className="ws__card" key={selected.id}>
                <FindingCard
                  finding={selected}
                  findingCount={findings.length}
                  selected
                  disposition={dispositions[selected.id] ?? null}
                  onDispositionChange={(next) => setDisposition(selected.id, next)}
                  comment={commentFor(
                    { editedComments: review?.editedComments ?? {} },
                    selected.id,
                    selected.commentDraft,
                  )}
                  commentEdited={review?.editedComments[selected.id] !== undefined}
                  onCommentChange={(next) => setComment(selected.id, next)}
                  onShowInDocument={() => showInDocument(selected)}
                  answer={
                    /* The one further question this finding could use, asked
                       inside the finding rather than in front of the review. */
                    rateAsk && picks && tables ? (
                      setAside.includes(RATE_SKIP_ID) ? (
                        <AnswerSetAside
                          note="Not checked: which of the two figures the plan’s own cost supports."
                          onAnswer={() => setCheckSkipped(RATE_SKIP_ID, false)}
                        />
                      ) : (
                        <InlineRateAnswer
                          prompt={rateAsk.prompt}
                          tables={tables}
                          rateTableId={picks.rateTableId}
                          onRateTable={(next) =>
                            setPicks({ ...picks, rateTableId: next })
                          }
                          running={checking}
                          onConfirm={() => {
                            const selection = selectionFrom(picks, tables, {
                              includeRate: true,
                            });
                            if (selection) void runWithSelection(selection, 'stay');
                          }}
                          onSkip={() => setCheckSkipped(RATE_SKIP_ID, true)}
                        />
                      )
                    ) : null
                  }
                />
              </div>
            ) : selectedPending ? (
              <div className="ws__card" key={selectedPending.id}>
                <PendingCheckCard row={selectedPending}>
                  {selectedPending.state === 'needs-you' &&
                  selectedPending.checkId === RECONCILE_CHECK_ID &&
                  picks &&
                  tables ? (
                    <InlineReconcile
                      tables={tables}
                      picks={picks}
                      onPicks={setPicks}
                      running={checking}
                      onConfirm={() => {
                        const selection = selectionFrom(picks, tables);
                        if (selection)
                          void runWithSelection(selection, 'first-finding');
                      }}
                      onSkip={() => setCheckSkipped(RECONCILE_CHECK_ID, true)}
                    />
                  ) : selectedPending.reason ? (
                    <CheckNotRun
                      reason={selectedPending.reason}
                      lookedFor={selectedPending.lookedFor}
                      onAnswer={
                        selectedPending.state === 'set-aside' &&
                        selectedPending.checkId === RECONCILE_CHECK_ID
                          ? () => setCheckSkipped(RECONCILE_CHECK_ID, false)
                          : undefined
                      }
                    />
                  ) : null}
                </PendingCheckCard>
              </div>
            ) : (
              <NoFindings />
            )}
          </main>

          <aside
            className="ws__doc-col"
            id={documentSheetId}
            data-scroll-pane
          >
            <div className="ws__panel-bar">
              <span className="ws__panel-title">Document</span>
              <button
                type="button"
                className="ws__panel-close"
                onClick={dismissDocumentSheet}
              >
                Close
              </button>
            </div>
            <Disclosure
              className="ws__doc-disc"
              headClassName="ws__doc-head"
              summary="Document"
              open={documentPaneOpen}
              onOpenChange={toggleDocumentPane}
              panelClassName="ws__doc-body"
            >
              <DocumentPane
                fileName={doc.name}
                finding={selected}
                pages={pages}
                loading={pagesLoading}
                page={review?.paneState.documentPage ?? null}
                onPageChange={showPage}
                onBackToFinding={
                  selected ? () => backToFinding(selected.id) : undefined
                }
                focusRequest={focusRequest}
                onFocusHandled={clearFocusRequest}
              />
              {doc.source ? (
                <ParseDetail document={doc.source} />
              ) : null}
            </Disclosure>
          </aside>
        </div>

        {/* Paper has no selection and nothing to expand, so what prints is
            not this screen but every finding with both layers open. Absent
            on screen entirely; see PrintReview.css. */}
        <PrintReview
          docName={doc.name}
          findings={findings}
          dispositions={dispositions}
          review={review}
          provenance={doc.provenance}
        />

        {/* The consequence of the first decision, and the only place the sheet
            announces itself. */}
        {includedCount > 0 ? (
          <div className="ws__sheet-strip">
            <span>
              {includedCount} comment{includedCount === 1 ? '' : 's'} ready.
            </span>
            <Link to={`/review/${doc.id}/comments`}>Preview the comment sheet.</Link>
          </div>
        ) : null}
      </div>
    </GlossaryProvider>
  );
}

/**
 * A review id this browser does not hold. Says why, and offers the two things
 * that are always available. Never a blank screen and never a raw 404.
 *
 * Deliberately not the EmptyState primitive: that primitive takes exactly one
 * action by design, and this state is specified with two. See
 * docs/redesign/ARCHITECTURE.md.
 */
function UnknownReview() {
  return (
    <>
      <Wordmark />
      <div className="ws__unknown">
        <h1 className="ws__unknown-head">That review isn’t in this browser.</h1>
        <p className="ws__unknown-body">
          Reviews are stored locally, so they don’t follow you between devices.
        </p>
        <div className="ws__unknown-actions">
          <Button variant="primary" marker to="/new">
            Start a new review
          </Button>
          <Button variant="secondary" marker to="/review/piscataway">
            Open the example
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * The wordmark alone. A working session hides the marketing navigation, so the
 * states that are not yet a working session — loading, and an id this browser
 * does not hold — still need one way back out that is not a decision.
 */
function Wordmark() {
  return (
    <header className="ws__head ws__head--bare">
      <Link to="/" className="ws__lockup">
        Ceiling
      </Link>
    </header>
  );
}

/**
 * No findings at all.
 *
 * Not an error, and never a clean bill of health. What Ceiling can say is that
 * fifteen specific patterns are not present; what it cannot say is anything
 * about the rest of the plan, and both halves are on the screen.
 *
 * Exported so the state can be rendered on its own, the way `YourReviews` is:
 * reaching it through the page would mean contriving a document that produces
 * nothing, and what matters here is the copy.
 */
export function NoFindings() {
  return (
    <div className="ws__none">
      <Label mono>Findings</Label>
      <h2 className="t-section">
        Ceiling found nothing on its fifteen checks in this document.
      </h2>
      <p className="ws__none-body">
        That means these fifteen specific patterns aren’t present — it doesn’t
        mean the plan is sound.
      </p>
      <div className="ws__none-actions">
        <Button variant="secondary" marker to="/checks">
          See what Ceiling checks for
        </Button>
        <Button variant="ghost" to="/method#limits">
          See what it can’t catch
        </Button>
      </div>
      {/* IA.md links the guide from three places, and this is one: a reviewer
          looking at an empty list is the reviewer most likely to be wondering
          what this was meant to do. */}
      <p className="ws__none-guide">
        <Link to="/guide">New here? Read the two-minute guide →</Link>
      </p>
    </div>
  );
}
